// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as z from 'zod';
import {
  ERROR_CODES,
  ERROR_CODE_MAPPING,
  ERROR_MESSAGES,
} from '@/api/constants';
import { ErrorCode } from '@/api/types';
import { AuthenticationSSOProvider } from '@/authentication/api/types';
import { followLoginRedirect } from '@/utilities/login-redirect';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

const authenticationDataSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  statusCode: z.number().optional(),
});

const UNKNOWN_LOGIN_ERROR_MESSAGE =
  'Unknown error, please try again or contact support for additional help.';
// An older backend sent a bare 403 for a deactivated member
const LEGACY_MEMBER_DEACTIVATED_STATUS_CODE = 403;

export function useLogin(redirectURL?: string) {
  const router = useRouter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const AUTHENTICATION_SSO_PATHS: Record<AuthenticationSSOProvider, string> = {
    google: '/auth/google/init',
    azureadv2: '/auth/azureadv2/init',
  };

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      // Ignore messages from other origins.
      if (event.origin !== import.meta.env.VITE_API_URL) {
        return;
      }

      const { data } = authenticationDataSchema.safeParse(event.data);
      if (data?.success) {
        setError(null);
        // The navigate function is asynchronous and returns a promise, but it
        // doesn't need to be awaited.
        followLoginRedirect(redirectURL, {
          toHome: () => void navigate({ to: '/assets' }),
          toRoute: (path) => {
            router.history.push(path);
          },
        });
      } else {
        const statusCode =
          data?.statusCode === LEGACY_MEMBER_DEACTIVATED_STATUS_CODE
            ? ERROR_CODE_MAPPING.organization_member_deactivated
            : data?.statusCode;
        const errorCode: ErrorCode | undefined =
          statusCode === undefined ? undefined : ERROR_CODES[statusCode];
        setError(
          errorCode === undefined
            ? UNKNOWN_LOGIN_ERROR_MESSAGE
            : ERROR_MESSAGES[errorCode]
        );
      }
    };

    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  }, [navigate, router, redirectURL, queryClient]);

  return {
    launchAuthentication: (provider: AuthenticationSSOProvider) => {
      const path = AUTHENTICATION_SSO_PATHS[provider];
      const url = new URL(path, import.meta.env.VITE_API_URL);

      window.open(url, '_sso');
    },
    error,
  };
}
